import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { motion } from 'framer-motion'
import { AlertCircle, Clock, ArrowRight } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { KYCWelcomeModal } from '@/components/KYCWelcomeModal'

interface KYCStatusCardProps {
  hasDocuments: boolean
}

export const KYCStatusCard: React.FC<KYCStatusCardProps> = ({ hasDocuments }) => {
  const { t } = useTranslation()
  const [isKYCModalOpen, setIsKYCModalOpen] = useState(false)

  if (hasDocuments) {
    // User has submitted documents, waiting for review
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 border border-blue-200 dark:border-blue-800 rounded-2xl p-6 mb-6"
      >
        <div className="flex items-start space-x-4">
          <div className="flex-shrink-0">
            <div className="w-12 h-12 bg-blue-100 dark:bg-blue-800 rounded-full flex items-center justify-center">
              <Clock className="w-6 h-6 text-blue-600 dark:text-blue-400" />
            </div>
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-blue-900 dark:text-blue-100 mb-2">
              {t('kycStatus.reviewPending.title', 'A sua conta ainda não foi revista')}
            </h3>
            <p className="text-blue-700 dark:text-blue-300 text-sm leading-relaxed mb-4">
              {t('kycStatus.reviewPending.message', 'Aguarde um período. Iremos rever a sua conta num prazo máximo de 1 dia útil.')}
            </p>
          </div>
        </div>
      </motion.div>
    )
  }

  // User hasn't submitted documents yet
  return (
    <>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-gradient-to-br from-orange-50 to-red-50 dark:from-orange-900/20 dark:to-red-900/20 border border-orange-200 dark:border-orange-800 rounded-2xl p-6 mb-6"
      >
        <div className="flex items-start space-x-4 mb-4">
          <div className="flex-shrink-0">
            <div className="w-12 h-12 bg-orange-100 dark:bg-orange-800 rounded-full flex items-center justify-center">
              <AlertCircle className="w-6 h-6 text-orange-600 dark:text-orange-400" />
            </div>
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-orange-900 dark:text-orange-100 mb-2">
              {t('kycStatus.noDocuments.title', 'Upss parece que ainda não temos a verificação da sua identidade')}
            </h3>
          </div>
        </div>

        <Button
          onClick={() => setIsKYCModalOpen(true)}
          className="w-full bg-gradient-to-r from-primary to-secondary hover:from-primary/90 hover:to-secondary/90 text-white font-medium py-3 px-6 rounded-xl transition-all duration-200 flex items-center justify-center space-x-2"
        >
          <span>{t('kycStatus.noDocuments.startButton', 'Vamos começar')}</span>
          <ArrowRight className="w-4 h-4" />
        </Button>
      </motion.div>

      <KYCWelcomeModal
        isOpen={isKYCModalOpen}
        onClose={() => setIsKYCModalOpen(false)}
        onComplete={() => {
          setIsKYCModalOpen(false)
          // Refresh user data to update KYC status
          window.location.reload()
        }}
      />
    </>
  )
}
